import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	OnDestroy
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DropdownBreadcrumItemSelected } from '../dropdown-breadcrum/dropdown-breadcrumb.component';
import { NavigationElement } from './navigation-element';
import { NavigationElementFactory } from './navigation-element.factory';
import { NavigationElementSelected } from './navigation-element-selected';
import { NavigationElementService } from './navigation-element.service';
import { Subscription } from 'rxjs';

let navigationIds = 0;

@Component({
	selector: 'cv-form-group-navigation',
	styleUrls: ['./form-group-navigation.component.scss'],
	templateUrl: './form-group-navigation.component.html'
})
export class FormGroupNavigationComponent implements OnInit, OnDestroy {

	/**
   * @public identifier for dropdown
   */
	public navigationId = `cv-from-group-navigation-${navigationIds++}`;

	navigationElements: Array<NavigationElement> = [];
	/**
   * @public property that object that will be navigated.
   */
	@Input() form: FormGroup;
	/**
   * @public callback to execute in order to get the `elementName` of the `NavigationElement`.
   */
	@Input() nameCallback: (...args) => string;

	/**
   * @public list of object keys that will flat an `NavigationElement` array when present.
   */
	@Input() formArrayKeysToFlat: Array<string> = [];

	/**
   * @public list of object keys that will be excluded from element children when found.
   */
	@Input() formArrayKeysExclusions: Array<string> = [];

	/**
   * @public list of object keys that will be excluded from element children when found.
   */
	@Input() clearOnElementSelected = false;

	/**
   * @public property that emit an event when a `NavigationElement` is selected
   */
	@Output() elementSelected: EventEmitter<
		NavigationElementSelected
	> = new EventEmitter<NavigationElementSelected>();

	/**
   * @public property that emits an event when a `NavigationElement` is selected
   */
	@Output() dropdownSelected: EventEmitter<
		Array<NavigationElement>
	> = new EventEmitter<Array<NavigationElement>>();

	/**
   * @public property that can dispose the `navigationElementsObservable` observable
   */
	private navigationElementsSubscription: Subscription;

	/**
   * @public property that can dispose the `clearNavigationElementsObservable` observable
   */
	private clearNavigationElementsSubscription: Subscription;

	constructor(
		private navigationElementFactory: NavigationElementFactory,
		private navigationElementService: NavigationElementService
	) {}

	ngOnInit(): void {
		this.navigationElementsSubscription = 
			this.navigationElementService.navigationElementsObservable.subscribe(navigationElements => {
				if (navigationElements.length) {
					this.navigationElements = [...navigationElements];
				} else {
					const navigationElement = this.navigationElementFactory.get(
						this.form,
						this.formArrayKeysExclusions,
						this.formArrayKeysToFlat,
						this.nameCallback
					);
					this.navigationElements = [navigationElement];
				}
			});

		this.clearNavigationElementsSubscription = 
			this.navigationElementService.clearNavigationElementsObservable.subscribe(shouldClear => {
				if (shouldClear) {
					this.navigationElements = this.navigationElements.slice(0, 1);
				}
			});
	}

	ngOnDestroy(): void {
		this.navigationElementsSubscription.unsubscribe();
		this.clearNavigationElementsSubscription.unsubscribe();
	}

	/**
   * dropdownItemSelected
   * bind itemSelected event of the component
   */
	dropdownItemSelected(
		itemSelected: DropdownBreadcrumItemSelected,
		navigationElementIndex: number
	): void {
		const navigationElement = this.navigationElementFactory.get(
			itemSelected.item,
			this.formArrayKeysExclusions,
			this.formArrayKeysToFlat,
			this.nameCallback
		);
		this.navigationElements = [
			...this.navigationElements.slice(0, navigationElementIndex + 1),
			navigationElement
		];
		this.dropdownSelected.emit(this.navigationElements);
	}

	/**
   * selectNavigationElement
   * method executed when you select a navigationElement
   */
	selectNavigationElement(
		event: any,
		navigationElementSelected: NavigationElement,
		navigationElementIndex: number
	): void {
		event.preventDefault();
		this.elementSelected.emit({
			index: navigationElementIndex,
			control: navigationElementSelected.control,
			path: this.navigationElements
				.slice(0, navigationElementIndex + 1)
				.map(element => ({
					name: element.name,
					index: element.index,
					key: element.key
				}))
		});
		if (this.clearOnElementSelected) {
			this.navigationElements = this.navigationElements.slice(0, navigationElementIndex + 1);
		}
	}

	/**
	 * Testing:
	 * List of items: // Recursive element
	 * 	jQuery('.cv-object-navigation-component .breadcrumb .breadcrumb-item')
	 * To get the path or to trigger the selection of the breadcrumb item: // can contain more than 1 action opener, be careful with the selector
	 * 	jQuery('.cv-object-navigation-component .breadcrumb .breadcrumb-item .breadcrumb-item-path').click()
	 * To open the breadcrumb options (items of that section): // can contain more than 1 action opener, be careful with the selector
	 * 	jQuery('.cv-object-navigation-component .breadcrumb .breadcrumb-item .cv-dropdown .action-toggle').click()
	 *   - Once you have opened the breadcrumb, have to proceed to select one of the items from the list.
	 * 	   jQuery('.cv-object-navigation-component .breadcrumb-item cv-dropdown-breadcrumb .cv-dropdown__options__list .action-select').click()
	 * 	 
	 *   The parent Object Navigation will be updated automatically.
	 */
}
